import { useReducer, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import type { GetPickingResponse, ScanRequest, ScannedBagInput, ManualScanResponse } from "@pm-barcode/shared";
import { ManualBagForm } from "../../components/manual-bag-form.tsx";
import { BagCountPrompt } from "../../components/bag-count-prompt.tsx";

/** A scanned non-serial bag awaiting a bag-count entry. */
interface PendingCount {
  productId: number;
  productName: string;
  lotId: number;
  lotName: string;
  moveId: number;
  sourceLocationId: number;
  destLocationId: number;
  tracking: string;
  batch: string;
  bagNumber?: string;
  weightKg: number;
}
import { useApiClient } from "../../hooks/use-api-client.ts";
import { RequestError } from "../../api/client.ts";
import { useScanner } from "../../hooks/use-scanner.ts";
import { bagTotal, useLeaveGuard } from "../../hooks/use-leave-guard.ts";

// ─── Audio ─────────────────────────────────────────────────────────────────

let audioCtx: AudioContext | null = null;

function beep(type: "success" | "error"): void {
  try {
    if (!audioCtx) audioCtx = new AudioContext();
    const play = () => {
      const osc = audioCtx!.createOscillator();
      const gain = audioCtx!.createGain();
      osc.connect(gain);
      gain.connect(audioCtx!.destination);
      osc.frequency.value = type === "success" ? 880 : 220;
      const now = audioCtx!.currentTime;
      gain.gain.setValueAtTime(0.3, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
      osc.start(now);
      osc.stop(now + 0.15);
    };
    if (audioCtx.state === "suspended") {
      void audioCtx.resume().then(play);
    } else {
      play();
    }
  } catch {
    // Ignore if audio is unavailable
  }
}

// ─── State machine ──────────────────────────────────────────────────────────

interface ScannedItem {
  productName: string;
  totalKg: number;
  bagCount: number;
}

type Feedback =
  | { kind: "ok"; productName: string; weightKg: number; batch: string; bagNumber?: string }
  | { kind: "err"; title: string };

type Modal =
  | { kind: "unknown"; rawBarcode: string }
  | { kind: "error"; title: string; message: string };

interface State {
  phase: "loading" | "load_error" | "ready" | "committing" | "done";
  picking: GetPickingResponse | null;
  errorMsg: string | null;
  scannedItems: Record<number, ScannedItem>;
  /** Per-bag inputs passed to the commit endpoint for the deferred Odoo write. */
  scannedBags: ScannedBagInput[];
  feedback: Feedback | null;
  modal: Modal | null;
  scanSeq: number;
}

type Action =
  | { type: "LOADED"; picking: GetPickingResponse }
  | { type: "LOAD_FAIL"; msg: string }
  | { type: "SCAN_OK"; productId: number; productName: string; weightKg: number; batch: string; bagNumber?: string; bag: ScannedBagInput }
  | { type: "SCAN_UNKNOWN"; rawBarcode: string }
  | { type: "SCAN_ERR"; title: string; msg: string }
  | { type: "DISMISS" }
  | { type: "COMMIT_START" }
  | { type: "COMMIT_OK" }
  | { type: "COMMIT_FAIL"; msg: string };

const initialState: State = {
  phase: "loading",
  picking: null,
  errorMsg: null,
  scannedItems: {},
  scannedBags: [],
  feedback: null,
  modal: null,
  scanSeq: 0,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "LOADED":
      return { ...state, phase: "ready", picking: action.picking };
    case "LOAD_FAIL":
      return { ...state, phase: "load_error", errorMsg: action.msg };
    case "SCAN_OK": {
      const prev = state.scannedItems[action.productId];
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: {
          kind: "ok",
          productName: action.productName,
          weightKg: action.weightKg,
          batch: action.batch,
          bagNumber: action.bagNumber,
        },
        scannedItems: {
          ...state.scannedItems,
          [action.productId]: {
            productName: action.productName,
            totalKg: (prev?.totalKg ?? 0) + action.weightKg, // weightKg is already the entry total
            bagCount: (prev?.bagCount ?? 0) + action.bag.quantity,
          },
        },
        scannedBags: [...state.scannedBags, action.bag],
        modal: null,
      };
    }
    case "SCAN_UNKNOWN":
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: { kind: "err", title: "Unknown barcode" },
        modal: { kind: "unknown", rawBarcode: action.rawBarcode },
      };
    case "SCAN_ERR":
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: { kind: "err", title: action.title },
        modal: { kind: "error", title: action.title, message: action.msg },
      };
    case "DISMISS":
      return { ...state, modal: null };
    case "COMMIT_START":
      return { ...state, phase: "committing" };
    case "COMMIT_OK":
      return { ...state, phase: "done" };
    case "COMMIT_FAIL":
      return { ...state, phase: "ready", errorMsg: action.msg };
    default:
      return state;
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

export function TransferScan() {
  const navigate = useNavigate();
  const api = useApiClient();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [showManual, setShowManual] = useState(false);
  const [pendingCount, setPendingCount] = useState<PendingCount | null>(null);
  const scanInFlight = useRef(false);

  // Confirm before leaving (← button, hardware back, reload) while there are uncommitted scans.
  const leave = useLeaveGuard(
    (state.phase === "ready" || state.phase === "committing") && state.scannedBags.length > 0,
  );

  // Confirm the bag count for a scanned non-serial (lot) product on a transfer.
  function confirmCount(count: number): void {
    const p = pendingCount;
    if (!p) return;
    beep("success");
    dispatch({
      type: "SCAN_OK",
      productId: p.productId,
      productName: p.productName,
      weightKg: p.weightKg * count, // total weight of the counted bags
      batch: p.batch,
      bagNumber: p.bagNumber,
      bag: {
        lotId: p.lotId,
        lotName: p.lotName,
        batch: p.batch,
        quantity: count,
        moveId: p.moveId,
        sourceLocationId: p.sourceLocationId,
        destLocationId: p.destLocationId,
        productId: p.productId,
        tracking: p.tracking,
      },
    });
    setPendingCount(null);
  }

  // Add a damaged-barcode bag (recorded via /scan/manual) to the transfer, like a scan.
  function onManualAdded(result: ManualScanResponse): void {
    const bag: ScannedBagInput = {
      lotId: result.odoo.lotId,
      lotName: result.odoo.lotName,
      batch: result.batch,
      quantity: result.odoo.qtyDone,
      moveId: result.odoo.moveId,
      sourceLocationId: result.odoo.sourceLocationId,
      destLocationId: result.odoo.destLocationId,
      productId: result.odoo.productId,
      tracking: result.odoo.tracking,
    };
    beep("success");
    dispatch({
      type: "SCAN_OK",
      productId: result.odoo.productId,
      productName: result.odoo.productName,
      weightKg: result.weightKg,
      batch: result.batch,
      bagNumber: result.bagNumber,
      bag,
    });
    setShowManual(false);
  }

  const { ref } = useParams({ strict: false }) as { ref: string };

  useEffect(() => {
    if (!api || !ref) return;
    let cancelled = false;
    api
      .getPicking(ref)
      .then((picking) => {
        if (!cancelled) dispatch({ type: "LOADED", picking });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          dispatch({
            type: "LOAD_FAIL",
            msg: err instanceof Error ? err.message : "Failed to load transfer",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, ref]);

  useScanner((event) => {
    if (state.phase !== "ready" || !api || scanInFlight.current || pendingCount) return;
    scanInFlight.current = true;

    const body: ScanRequest = {
      rawBarcode: event.rawBarcode,
      symbology: event.symbology,
      workflow: "transfer",
      pickingRef: ref,
      deviceId: import.meta.env.VITE_DEVICE_ID,
      capturedAt: event.capturedAt,
    };

    void api
      .postScan(body)
      .then((result) => {
        if (result.outcome === "ok") {
          // Non-serial (lot) products: prompt for the number of bags instead of re-scanning.
          if (result.odoo.tracking !== "serial") {
            beep("success");
            setPendingCount({
              productId: result.odoo.productId,
              productName: result.odoo.productName,
              lotId: result.odoo.lotId,
              lotName: result.odoo.lotName,
              moveId: result.odoo.moveId,
              sourceLocationId: result.odoo.sourceLocationId,
              destLocationId: result.odoo.destLocationId,
              tracking: result.odoo.tracking,
              batch: result.normalised.batch,
              bagNumber: result.normalised.bagNumber,
              weightKg: result.normalised.weightKg,
            });
            return;
          }
          beep("success");
          dispatch({
            type: "SCAN_OK",
            productId: result.odoo.productId,
            productName: result.odoo.productName,
            weightKg: result.normalised.weightKg,
            batch: result.normalised.batch,
            bagNumber: result.normalised.bagNumber,
            bag: {
              lotId: result.odoo.lotId,
              lotName: result.odoo.lotName,
              batch: result.normalised.batch,
              quantity: result.odoo.qtyDone, // 1 unit per bag — the kg weight is display-only
              moveId: result.odoo.moveId,
              sourceLocationId: result.odoo.sourceLocationId,
              destLocationId: result.odoo.destLocationId,
              productId: result.odoo.productId,
              tracking: result.odoo.tracking,
            },
          });
        } else if (result.outcome === "unknown") {
          beep("error");
          dispatch({ type: "SCAN_UNKNOWN", rawBarcode: result.rawBarcode });
        } else {
          beep("error");
          dispatch({ type: "SCAN_ERR", title: "Scan failed", msg: "Unexpected scan outcome" });
        }
      })
      .catch((err: unknown) => {
        beep("error");
        const code = err instanceof RequestError ? err.code : undefined;
        dispatch({
          type: "SCAN_ERR",
          title: code === "already_picked" ? "Already moved" : code === "not_in_stock" ? "Not in stock" : "Scan failed",
          msg: err instanceof Error ? err.message : "Unknown error",
        });
      })
      .finally(() => {
        scanInFlight.current = false;
      });
  });

  async function handleCommit() {
    if (!api || state.phase !== "ready" || !state.picking) return;
    dispatch({ type: "COMMIT_START" });
    try {
      await api.commitTransfer(state.picking.picking.id, state.scannedBags);
      dispatch({ type: "COMMIT_OK" });
    } catch (err) {
      dispatch({
        type: "COMMIT_FAIL",
        msg: err instanceof Error ? err.message : "Commit failed",
      });
    }
  }



  // ── Loading ──
  if (state.phase === "loading") {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-gray-400">Loading transfer…</p>
      </div>
    );
  }

  // ── Load error ──
  if (state.phase === "load_error") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-lg font-semibold text-red-400">Cannot open transfer</p>
        <p className="text-sm text-gray-400">{state.errorMsg}</p>
        <button
          onClick={() => void navigate({ to: "/transfer" })}
          className="rounded-xl bg-gray-800 px-6 py-3 font-semibold active:bg-gray-700"
        >
          Back to transfers
        </button>
      </div>
    );
  }

  // ── Done ──
  if (state.phase === "done") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-6 p-6 text-center">
        <div className="text-6xl text-purple-400">✓</div>
        <div>
          <p className="text-xl font-bold">Transfer committed</p>
          <p className="mt-1 font-mono text-gray-300">{ref}</p>
        </div>
        <button
          onClick={() => void navigate({ to: "/workflow" })}
          className="w-full max-w-xs rounded-xl bg-purple-600 py-4 text-lg font-semibold active:bg-purple-700"
        >
          Back to workflow
        </button>
      </div>
    );
  }

  // ── Ready / Committing ──
  const picking = state.picking!;
  const scannedEntries = Object.entries(state.scannedItems) as [string, ScannedItem][];

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-gray-800 px-4 py-3">
        <button
          onClick={() => void navigate({ to: "/transfer" })}
          className="text-sm text-gray-400 active:text-white"
        >
          ←
        </button>
        <div className="flex-1">
          <p className="font-mono text-sm font-semibold">{picking.picking.name}</p>
          <p className="text-xs text-gray-500">
            {picking.picking.sourceLocation.name} → {picking.picking.destLocation.name}
          </p>
        </div>
        <span className="rounded-full bg-purple-900/50 px-2 py-0.5 text-xs font-semibold uppercase tracking-wider text-purple-300">
          Transfer
        </span>
      </div>

      {/* Commit error */}
      {state.errorMsg && (
        <div className="mx-4 mt-3 rounded-xl bg-red-900/40 px-4 py-2 text-sm text-red-300">
          {state.errorMsg}
        </div>
      )}

      {/* Scanned items list */}
      <div className="flex-1 overflow-y-auto">
        {scannedEntries.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <p className="text-sm text-gray-500">No bags scanned yet</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="sticky top-0 bg-gray-900">
              <tr className="border-b border-gray-800 text-xs uppercase tracking-widest text-gray-500">
                <th className="py-2 pl-4 text-left font-normal">Product</th>
                <th className="py-2 pr-3 text-right font-normal">Bags</th>
                <th className="py-2 pr-4 text-right font-normal">Kg</th>
              </tr>
            </thead>
            <tbody>
              {scannedEntries.map(([productId, item]) => (
                <tr key={productId} className="border-b border-gray-800/60">
                  <td className="py-3 pl-4 text-sm leading-tight">{item.productName}</td>
                  <td className="py-3 pr-3 text-right tabular-nums text-gray-400">
                    {item.bagCount}
                  </td>
                  <td className="py-3 pr-4 text-right font-semibold tabular-nums text-purple-300">
                    {item.totalKg}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Scan feedback panel */}
      <div className="border-t border-gray-800 p-4">
        <div
          key={state.scanSeq}
          className={`rounded-2xl p-4 text-center transition-colors ${
            state.feedback?.kind === "ok"
              ? "bg-scan-ok/20 animate-flash"
              : state.feedback?.kind === "err"
                ? "bg-scan-error/20 animate-flash"
                : "bg-gray-900"
          }`}
        >
          {state.feedback?.kind === "ok" ? (
            <>
              <p className="text-2xl font-bold text-scan-ok">
                ✓ {state.feedback.weightKg} kg
              </p>
              <p className="mt-0.5 text-sm text-gray-300">{state.feedback.productName}</p>
              <p className="text-xs text-gray-500">
                Batch {state.feedback.batch}
                {state.feedback.bagNumber ? ` · Bag ${state.feedback.bagNumber}` : ""}
              </p>
            </>
          ) : state.feedback?.kind === "err" ? (
            <p className="font-semibold text-scan-error">{state.feedback.title}</p>
          ) : (
            <p className="text-sm text-gray-500">Ready to scan</p>
          )}
        </div>
      </div>

      {/* Commit button */}
      <div className="px-4 pb-6">
        <button
          onClick={() => setShowManual(true)}
          className="mb-2 w-full text-sm text-gray-400 underline active:text-white"
        >
          Damaged barcode? Add bag manually
        </button>
        <button
          onClick={() => void handleCommit()}
          disabled={state.phase === "committing" || scannedEntries.length === 0}
          className="w-full rounded-xl bg-purple-600 py-4 text-lg font-semibold active:bg-purple-700 disabled:opacity-40"
        >
          {state.phase === "committing" ? "Committing…" : "Commit Transfer"}
        </button>
      </div>

      {/* Bag-count prompt for a scanned non-serial (lot) product */}
      {pendingCount && (
        <BagCountPrompt
          productName={pendingCount.productName}
          batch={pendingCount.batch}
          bagNumber={pendingCount.bagNumber}
          onConfirm={confirmCount}
          onCancel={() => setPendingCount(null)}
        />
      )}

      {/* Manual bag entry (damaged barcode). Products = those already scanned this session. */}
      {showManual && (
        <ManualBagForm
          workflow="transfer"
          pickingRef={ref}
          products={(Object.entries(state.scannedItems) as [string, { productName: string }][]).map(([pid, item]) => ({
            id: Number(pid),
            name: item.productName,
            tracking: state.scannedBags.find((b) => b.productId === Number(pid))?.tracking,
          }))}
          defaultBatch={state.scannedBags[state.scannedBags.length - 1]?.batch}
          defaultProductId={state.scannedBags[state.scannedBags.length - 1]?.productId}
          onAdded={onManualAdded}
          onClose={() => setShowManual(false)}
        />
      )}

      {/* Exception modal */}
      {state.modal && (
        <ExceptionModal modal={state.modal} onDismiss={() => dispatch({ type: "DISMISS" })} />
      )}

      {/* Abandon confirmation */}
      {leave.status === "blocked" && (
        <div className="fixed inset-0 flex items-end justify-center bg-black/70 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-gray-800 p-6">
            <h3 className="mb-2 text-lg font-bold text-red-400">Abandon transfer?</h3>
            <p className="mb-5 text-sm text-gray-400">
              The <span className="font-semibold text-white">{bagTotal(state.scannedBags)} bag{bagTotal(state.scannedBags) !== 1 ? "s" : ""}</span> scanned this session have not been committed yet.
              Leaving now will lose them — tap Commit Transfer first to save.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => leave.reset()}
                className="flex-1 rounded-xl bg-gray-700 py-3 font-semibold active:bg-gray-600"
              >
                Keep scanning
              </button>
              <button
                onClick={() => leave.proceed()}
                className="flex-1 rounded-xl bg-red-700 py-3 font-semibold active:bg-red-600"
              >
                Abandon
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Exception modal ─────────────────────────────────────────────────────────

interface ExceptionModalProps {
  modal: Modal;
  onDismiss: () => void;
}

function ExceptionModal({ modal, onDismiss }: ExceptionModalProps) {
  const title = modal.kind === "unknown" ? "Unknown Barcode" : modal.title;
  const body =
    modal.kind === "unknown" ? (
      <>
        <p className="text-gray-300">
          This barcode was not recognised and has been flagged for review.
        </p>
        <p className="mt-2 break-all font-mono text-xs text-gray-500">{modal.rawBarcode}</p>
        <p className="mt-1 font-mono text-xs text-gray-600">
          {/* eslint-disable-next-line no-control-regex */}
          {modal.rawBarcode.length} chars · {(modal.rawBarcode.match(/\x1d/g) ?? []).length} GS
        </p>
      </>
    ) : (
      <p className="text-sm text-gray-400">{modal.message}</p>
    );

  return (
    <div className="fixed inset-0 flex items-end justify-center bg-black/70 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-gray-800 p-6">
        <h3 className="mb-3 text-lg font-bold text-scan-error">{title}</h3>
        <div className="mb-5">{body}</div>
        <button
          onClick={onDismiss}
          className="w-full rounded-xl bg-purple-600 py-3 font-semibold active:bg-purple-700"
        >
          Continue scanning
        </button>
      </div>
    </div>
  );
}
